import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import FileDropzone from "@/components/FileDropzone";
import { createSuggestion } from "@/lib/api";
import { useSkillPath } from "@/lib/skillPath";
import { useAuth } from "@/lib/auth-context";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

export default function SuggestPage() {
  useDocumentTitle("SkillPatch");
  const slug = String(useParams().skillSlug);
  const navigate = useNavigate();
  const skillHref = useSkillPath()(slug);
  const { user, accessToken } = useAuth();
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [mode, setMode] = useState<"describe" | "archive">("describe");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Gating is handled by the <RequireAuth> route guard; kept as a defensive /
  // type-narrowing check (the page never mounts for a logged-out user).
  if (!user || !accessToken) return null;

  const isArchive = mode === "archive";
  const ready = Boolean(title.trim()) && (isArchive ? Boolean(file) : Boolean(message.trim()));

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      // Submit with the logged-in session — the backend tags it source=user (agents use an API key).
      // In archive mode the uploaded files are the proposal, so the title doubles as the summary.
      const summary = isArchive ? title.trim() : message.trim();
      await createSuggestion(slug, title.trim(), summary, accessToken!, isArchive ? file : null);
      // Go back to the skill we came from (its queue shows the new suggestion progressing). Using
      // history back — not a push — so the skill's own Back button then returns to the true origin.
      navigate(-1);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit");
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto max-w-3xl px-6 py-12">
      <Link
        to={skillHref}
        onClick={(e) => {
          // Go back where we came from (usually the skill) rather than pushing a duplicate skill
          // entry the skill's own Back button would then bounce into.
          e.preventDefault();
          navigate(-1);
        }}
        className="inline-flex items-center gap-1.5 text-sm text-text/55 transition hover:text-accent"
      >
        <BackIcon /> Back to <span className="font-medium first-letter:uppercase">{slug}</span>
      </Link>

      {/* Hero */}
      <header className="mt-6 text-center">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium text-accent">
          <SparkIcon /> Suggestion
        </span>
        <h1 className="mt-4 bg-gradient-to-br from-white to-accent/70 bg-clip-text font-display text-3xl font-bold tracking-tight text-transparent">
          Make a Suggestion
        </h1>
        <p className="mx-auto mt-2 max-w-xl text-sm text-text/55">
          Write down the improvement you'd like to see for{" "}
          <span className="font-medium text-text/80 first-letter:uppercase">{slug}</span>. An agent
          turns it into edits, then it runs <b className="text-text/80">Critic → Security</b>{" "}
          and the owner approves before anything ships.
        </p>
      </header>

      <form onSubmit={onSubmit} className="mt-9 space-y-6">
        {/* Title */}
        <section>
          <label htmlFor="sg-title" className="text-sm font-semibold text-text">Title<span className="ml-0.5 text-red-400" title="Required">*</span></label>
          <input
            id="sg-title"
            placeholder="e.g. Document commit squashing"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="mt-2 w-full rounded-xl border border-white/15 bg-white/[0.015] px-3.5 py-2.5 text-sm outline-none transition focus:border-primary"
          />
        </section>

        {/* Choose how to convey the change: describe it, or upload an edited archive */}
        <div className="inline-flex w-full rounded-xl border border-white/10 bg-white/[0.03] p-1 text-sm font-medium">
          {([
            { id: "describe" as const, label: "Describe the change" },
            { id: "archive" as const, label: "Upload edited archive" },
          ]).map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => { setMode(o.id); setError(null); }}
              className={`flex-1 rounded-lg px-3 py-2 transition ${
                mode === o.id ? "bg-primary text-white" : "text-text/55 hover:text-text/80"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>

        {/* Describe mode — plain-English request the Feedback agent turns into edits */}
        {!isArchive && (
          <section>
            <label htmlFor="sg-msg" className="text-sm font-semibold text-text">What should change?<span className="ml-0.5 text-red-400" title="Required">*</span></label>
            <textarea
              id="sg-msg"
              placeholder="Describe the change in plain English — e.g. Add a section explaining how to squash commits before finalizing, with a short example."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={8}
              className="mt-2 w-full resize-y rounded-xl border border-white/15 bg-white/[0.015] px-3.5 py-2.5 text-sm leading-relaxed outline-none transition focus:border-primary placeholder:text-text/35"
            />
            <p className="mt-2 text-xs text-text/45">
              Be specific about <em>what</em> to add or fix and <em>why</em> — the clearer the request, the better the proposed edit.
            </p>
          </section>
        )}

        {/* Archive mode — the uploaded files are the proposal directly */}
        {isArchive && (
          <section>
            <label className="text-sm font-semibold text-text">Edited skill archive<span className="ml-0.5 text-red-400" title="Required">*</span></label>
            <p className="mt-1 text-xs text-text/45">
              Attach the modified skill — your files become the proposal directly, skipping the automatic edit step.
            </p>
            <div className="mt-3">
              <FileDropzone
                file={file}
                onPick={setFile}
                accept=".tar,.tgz,.tar.gz,application/x-tar,application/gzip"
                extensions={[".tar", ".tgz", ".tar.gz"]}
                maxBytes={10 * 1024 * 1024}
                hint=".tar, .tgz or .tar.gz containing SKILL.md · max 10 MB"
              />
            </div>
          </section>
        )}

        {/* How it works */}
        <div className="rounded-xl border border-white/10 bg-white/[0.015] p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-text/45">What happens next</p>
          <ol className="mt-2.5 space-y-1.5 text-sm text-text/60">
            {isArchive ? (
              <li><span className="text-accent">1.</span> Your <b className="text-text/80">uploaded files are used as-is</b> — we derive the version bump and changelog from the diff, no re-authoring.</li>
            ) : (
              <li><span className="text-accent">1.</span> Your suggestion is <b className="text-text/80">turned into proposed file changes</b> automatically — no manual editing needed.</li>
            )}
            <li><span className="text-accent">2.</span> A <b className="text-text/80">structure and safety check</b> is carried out to make sure the changes are well-formed and secure.</li>
            <li><span className="text-accent">3.</span> The owner sees a diff and <b className="text-text/80">approves</b> — it merges as a new version, crediting you.</li>
          </ol>
        </div>

        {error && (
          <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">{error}</p>
        )}

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={busy || !ready}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-primary to-accent px-4 py-3 font-semibold text-white shadow-lg shadow-primary/20 transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none"
          >
            {busy ? "Submitting…" : "Submit suggestion"}
          </button>
          <Link
            to={skillHref}
            className="rounded-xl border border-white/15 px-4 py-3 text-sm text-text/70 transition hover:bg-white/5"
          >
            Cancel
          </Link>
        </div>

        <p className="text-center text-xs text-text/40">
          Submitted — the owner reviews and approves before anything ships.
        </p>
      </form>
    </main>
  );
}

function SparkIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2l1.6 5.4L19 9l-5.4 1.6L12 16l-1.6-5.4L5 9l5.4-1.6z" />
    </svg>
  );
}
function BackIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}
